import { BriefcaseBusiness } from 'lucide-react'
import { type ComponentProps } from 'react'

import DisplayTooltipOnHover from 'components/reusable/DisplayTooltipOnHover'
import { Badge } from 'slab'
import { type AdminEmployer, AdminEmployerReviewStatus, type BrokersBrokerMemberDenormalized } from 'utils/web_api.schemas'

function getBadgeTypeInfo (
  employerReviewStatus?: string,
  shortened?: boolean
): {
  type: ComponentProps<typeof Badge>['type']
  text: string
  tooltipText: string
} | null {
  if (!employerReviewStatus) {
    return null
  }

  switch (employerReviewStatus) {
    case AdminEmployerReviewStatus.denied:
      return {
        type: 'critical',
        text: shortened ? 'Denied' : 'Employer denied',
        tooltipText: 'This employer has been denied during the review process',
      }
    case AdminEmployerReviewStatus.inactive_duplicate:
    case AdminEmployerReviewStatus.inactive_withdrawn:
    case AdminEmployerReviewStatus.inactive_ineligible:
    case AdminEmployerReviewStatus.inactive_churned:
      return {
        type: 'critical',
        text: shortened
          ? employerReviewStatus
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ')
          : `Employer marked ${employerReviewStatus
              .split('_')
              .map(word => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' ')}`,
        tooltipText: `This employer has been denied (${employerReviewStatus}) during the review process`,
      }
    case AdminEmployerReviewStatus.pending:
      return {
        type: 'warning',
        text: shortened ? 'Under review' : 'Employer under review',
        tooltipText: 'This employer is currently under review',
      }
    case AdminEmployerReviewStatus.approved:
      return {
        type: 'success',
        text: shortened ? 'Approved' : 'Employer approved',
        tooltipText: 'This employer has been approved during the review process',
      }
    default:
      return {
        type: 'neutral',
        text: shortened ? 'Unknown' : `Employer review status: ${employerReviewStatus}`,
        tooltipText: `Unknown employer review status: ${employerReviewStatus}`,
      }
  }
}

const EmployerReviewStatusBadge = ({
  member,
  employer,
  shortened = false,
}: {
  member?: BrokersBrokerMemberDenormalized
  employer?: AdminEmployer
  shortened?: boolean
}) => {
  const reviewStatus = member?.employer_review_status || employer?.review_status
  const badgeInfo = getBadgeTypeInfo(reviewStatus, shortened)

  if (!badgeInfo) {
    return null
  }

  const { type, text, tooltipText } = badgeInfo

  return (
    <DisplayTooltipOnHover text={tooltipText} asChild>
      <Badge type={type} leftIcon={BriefcaseBusiness}>{text}</Badge>
    </DisplayTooltipOnHover>
  )
}

export default EmployerReviewStatusBadge
